import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Button, InlineNotification, Loading, Modal, Select, SelectItem, Tag, TextInput, Toggle } from '@carbon/react';
import { EntryScheduleLabel, LinkShiftModal } from 'src/components/Schedule/EntrySchedule';
import {
  breakSpan,
  formatMinutes,
  localTime,
  timesheetErrorKey,
  toIsoFromLocal,
} from 'src/components/Timesheet/timesheetFormat';
import { useTenantContext } from 'src/context/useTenantContext';
import { useMoney } from 'src/hooks/useMoney';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { TimesheetDayEntry, TimesheetView, useTimesheet, useTimesheetActions } from 'src/hooks/useTimesheets';
import { useTransactionCategories } from 'src/hooks/useTransactionCategories';
import { TransactionCategoryTypeEnum } from 'src/utils/accounting';

const DATE = 'YYYY-MM-DD';
const STATUS_TAG = { OPEN: 'blue', APPROVED: 'green', PAID: 'gray' } as const;

type DialogState =
  | { kind: 'entry'; entry: TimesheetDayEntry | null; date: string }
  | { kind: 'break'; entry: TimesheetDayEntry }
  | { kind: 'delete'; entry: TimesheetDayEntry }
  | { kind: 'reopen' }
  | { kind: 'pay' }
  | { kind: 'link'; entry: TimesheetDayEntry; date: string }
  | null;

/** Two wall-clock times on `date` → ISO pair; an end at or before the start means "next day". */
function span(date: string, from: string, to: string, tz: string) {
  const start = toIsoFromLocal(date, from, tz);
  let end = toIsoFromLocal(date, to, tz);
  if (end <= start) end = toIsoFromLocal(dayjs(date).add(1, 'day').format(DATE), to, tz);
  return { start, end };
}

function EntryModal(props: {
  view: TimesheetView;
  entry: TimesheetDayEntry | null;
  date: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tz = props.view.rules.timezone;
  const actions = useTimesheetActions(props.view.timesheet.id);
  const [date, setDate] = useState(props.entry ? dayjs(props.entry.clockInAt).tz(tz).format(DATE) : props.date);
  const [from, setFrom] = useState(props.entry ? localTime(props.entry.clockInAt, tz) : '');
  const [to, setTo] = useState(props.entry?.clockOutAt ? localTime(props.entry.clockOutAt, tz) : '');
  const [shiftName, setShiftName] = useState(props.entry?.shiftName ?? '');
  const [note, setNote] = useState(props.entry?.note ?? '');
  const mutation = props.entry ? actions.updateEntry : actions.createEntry;

  const submit = () => {
    const { start, end } = span(date, from, to, tz);
    const body = { clockInAt: start, clockOutAt: end, shiftName: shiftName || null, note: note || null };
    if (props.entry) {
      actions.updateEntry.mutate({ entryId: props.entry.id, body }, { onSuccess: props.onClose });
    } else {
      actions.createEntry.mutate(
        {
          ...body,
          employeeId: props.view.header.employeeId,
          shiftName: shiftName || undefined,
          note: note || undefined,
        },
        { onSuccess: props.onClose }
      );
    }
  };

  return (
    <Modal
      open
      passiveModal
      modalHeading={props.entry ? t('timesheet.actions.edit') : t('timesheet.actions.addEntry')}
      onRequestClose={props.onClose}
    >
      <TextInput
        id="entry-date"
        type="date"
        labelText={t('timesheet.columns.date')}
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      <TextInput
        id="entry-in"
        type="time"
        labelText={t('timesheet.columns.clockIn')}
        value={from}
        onChange={(e) => setFrom(e.target.value)}
      />
      <TextInput
        id="entry-out"
        type="time"
        labelText={t('timesheet.columns.clockOut')}
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <TextInput
        id="entry-shift"
        labelText={t('timesheet.shift')}
        value={shiftName}
        maxLength={60}
        onChange={(e) => setShiftName(e.target.value)}
      />
      <TextInput
        id="entry-note"
        labelText={t('timesheet.taskNote')}
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.target.value)}
      />
      {mutation.error && (
        <InlineNotification kind="error" lowContrast hideCloseButton title={t(timesheetErrorKey(mutation.error))} />
      )}
      <Button data-testid="entry-submit" disabled={!date || !from || !to || mutation.isLoading} onClick={submit}>
        {t('actions.save')}
      </Button>
    </Modal>
  );
}

function BreakModal(props: { view: TimesheetView; entry: TimesheetDayEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const tz = props.view.rules.timezone;
  const actions = useTimesheetActions(props.view.timesheet.id);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [paid, setPaid] = useState(false);

  const submit = () => {
    const { start, end } = breakSpan(props.entry.clockInAt, from, to, tz);
    actions.addBreak.mutate(
      { entryId: props.entry.id, body: { startAt: start, endAt: end, paid } },
      { onSuccess: props.onClose }
    );
  };

  return (
    <Modal open passiveModal modalHeading={t('timesheet.actions.addBreak')} onRequestClose={props.onClose}>
      <TextInput
        id="break-from"
        type="time"
        labelText={t('timesheet.columns.clockIn')}
        value={from}
        onChange={(e) => setFrom(e.target.value)}
      />
      <TextInput
        id="break-to"
        type="time"
        labelText={t('timesheet.columns.clockOut')}
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <Toggle
        id="break-paid"
        labelText={t('timesheet.paidBreak')}
        toggled={paid}
        onToggle={(checked: boolean) => setPaid(checked)}
      />
      {actions.addBreak.error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t(timesheetErrorKey(actions.addBreak.error))}
        />
      )}
      <Button disabled={!from || !to || actions.addBreak.isLoading} onClick={submit}>
        {t('actions.save')}
      </Button>
    </Modal>
  );
}

function PayModal(props: { view: TimesheetView; onClose: () => void }) {
  const { t } = useTranslation();
  const money = useMoney();
  const actions = useTimesheetActions(props.view.timesheet.id);
  const restaurant = useCurrentRestaurant();
  const { data: categories } = useTransactionCategories();
  const expense = (categories?.data ?? []).filter((c) => Number(c.type) === TransactionCategoryTypeEnum.EXPENSE);
  const [paidAt, setPaidAt] = useState(dayjs().format(DATE));
  const [method, setMethod] = useState<'CASH' | 'TRANSFER' | 'OTHER'>('CASH');
  const [categoryId, setCategoryId] = useState(restaurant?.timesheetExpenseCategoryId ?? expense[0]?.id ?? '');

  return (
    <Modal open passiveModal modalHeading={t('timesheet.actions.markPaid')} onRequestClose={props.onClose}>
      <p>
        {t('timesheet.grossPay')}: <strong>{money.format(props.view.computation.totals.pay)}</strong>
      </p>
      <TextInput
        id="pay-date"
        type="date"
        labelText={t('timesheet.paidAt')}
        value={paidAt}
        onChange={(e) => setPaidAt(e.target.value)}
      />
      <Select
        id="pay-method"
        labelText={t('timesheet.paymentMethod')}
        value={method}
        onChange={(e) => setMethod(e.target.value as typeof method)}
      >
        {(['CASH', 'TRANSFER', 'OTHER'] as const).map((m) => (
          <SelectItem key={m} value={m} text={t(`timesheet.methods.${m}`)} />
        ))}
      </Select>
      <Select
        id="pay-category"
        data-testid="timesheet-pay-category"
        labelText={t('timesheet.expenseCategory')}
        value={categoryId}
        onChange={(e) => setCategoryId(e.target.value)}
      >
        {expense.map((c) => (
          <SelectItem key={c.id} value={c.id} text={c.name} />
        ))}
      </Select>
      {actions.pay.error && (
        <InlineNotification kind="error" lowContrast hideCloseButton title={t(timesheetErrorKey(actions.pay.error))} />
      )}
      <Button
        data-testid="timesheet-pay-submit"
        disabled={!categoryId || actions.pay.isLoading}
        onClick={() =>
          actions.pay.mutate(
            { paidAt: toIsoFromLocal(paidAt, '12:00', props.view.rules.timezone), method, categoryId },
            { onSuccess: props.onClose }
          )
        }
      >
        {t('timesheet.actions.markPaid')}
      </Button>
    </Modal>
  );
}

function ReopenModal(props: { view: TimesheetView; onClose: () => void }) {
  const { t } = useTranslation();
  const actions = useTimesheetActions(props.view.timesheet.id);
  const [reason, setReason] = useState('');
  return (
    <Modal open passiveModal modalHeading={t('timesheet.actions.reopen')} onRequestClose={props.onClose}>
      <TextInput
        id="reopen-reason"
        data-testid="timesheet-reopen-reason"
        labelText={t('timesheet.reopenReason')}
        value={reason}
        maxLength={500}
        onChange={(e) => setReason(e.target.value)}
      />
      <Button
        kind="danger"
        data-testid="timesheet-reopen-submit"
        disabled={!reason.trim() || actions.reopen.isLoading}
        onClick={() => actions.reopen.mutate(reason.trim(), { onSuccess: props.onClose })}
      >
        {t('timesheet.actions.reopen')}
      </Button>
    </Modal>
  );
}

const EmployeeTimesheetDetail = () => {
  const { t } = useTranslation();
  const money = useMoney();
  const { timesheetId } = useParams();
  const { buildPath } = useTenantContext();
  const { data: view, isLoading } = useTimesheet(timesheetId);
  const actions = useTimesheetActions(timesheetId ?? '');
  const [modal, setModal] = useState<DialogState>(null);

  if (isLoading || !view) return <Loading withOverlay={false} />;

  const { timesheet, header, computation, rules } = view;
  const tz = rules.timezone;
  const open = timesheet.status === 'OPEN';
  // Approval locks the sheet, so the server refuses it until the period's last local day has passed.
  const periodEnded = dayjs().tz(tz).format(DATE) > timesheet.periodEnd;
  const close = () => setModal(null);
  const stamp = (iso: string | null) => (iso ? dayjs(iso).tz(tz).format('DD/MM/YYYY HH:mm') : '');

  return (
    <div className="timesheet-detail">
      <Link className="timesheet-detail__back" to={buildPath('employees/timesheets')}>
        ← {t('common.timesheets')}
      </Link>

      <section className="timesheet-detail__header" data-testid="timesheet-header">
        <h2>{t('timesheet.title')}</h2>
        <dl>
          <dt>{t('timesheet.header.employee')}</dt>
          <dd>{header.employeeName}</dd>
          <dt>{t('timesheet.header.employeeId')}</dt>
          <dd>{header.employeeId.slice(0, 8)}</dd>
          <dt>{t('timesheet.header.jobTitle')}</dt>
          <dd>
            {t(`timesheet.header.roles.${header.role}`)} ·{' '}
            {header.partTime ? t('timesheet.header.partTime') : t('timesheet.header.fullTime')}
          </dd>
          <dt>{t('timesheet.header.manager')}</dt>
          <dd>{header.managerName ?? '—'}</dd>
          <dt>{t('timesheet.header.period')}</dt>
          <dd>
            {dayjs(header.periodStart).format('DD/MM/YYYY')} – {dayjs(header.periodEnd).format('DD/MM/YYYY')}
          </dd>
        </dl>
        <Tag type={STATUS_TAG[timesheet.status]}>{t(`timesheet.status.${timesheet.status}`)}</Tag>
        {computation.warnings.map((w) => (
          <Tag key={w} type="red">
            {t(`timesheet.warnings.${w}`)}
          </Tag>
        ))}
      </section>

      <div className="timesheet-detail__actions">
        {open && (
          <Button
            kind="tertiary"
            data-testid="timesheet-add-entry"
            onClick={() => setModal({ kind: 'entry', entry: null, date: header.periodStart })}
          >
            {t('timesheet.actions.addEntry')}
          </Button>
        )}
        {open && periodEnded && (
          <Button
            data-testid="timesheet-approve"
            disabled={actions.approve.isLoading}
            onClick={() => actions.approve.mutate()}
          >
            {t('timesheet.actions.approve')}
          </Button>
        )}
        {timesheet.status === 'APPROVED' && (
          <>
            <Button data-testid="timesheet-pay" onClick={() => setModal({ kind: 'pay' })}>
              {t('timesheet.actions.markPaid')}
            </Button>
            <Button kind="danger--tertiary" data-testid="timesheet-reopen" onClick={() => setModal({ kind: 'reopen' })}>
              {t('timesheet.actions.reopen')}
            </Button>
          </>
        )}
        <Button kind="ghost" onClick={() => window.print()}>
          {t('timesheet.actions.print')}
        </Button>
      </div>
      {actions.approve.error ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t(timesheetErrorKey(actions.approve.error))}
        />
      ) : null}

      <table className="timesheet-detail__log">
        <thead>
          <tr>
            <th>{t('timesheet.columns.date')}</th>
            <th>{t('timesheet.columns.weekday')}</th>
            <th>{t('timesheet.columns.shift')}</th>
            <th>{t('timesheet.columns.clockIn')}</th>
            <th>{t('timesheet.columns.clockOut')}</th>
            <th>{t('timesheet.columns.breaks')}</th>
            <th>{t('timesheet.columns.hours')}</th>
            <th>{t('timesheet.columns.pay')}</th>
            {open && <th aria-label={t('common.actions')} />}
          </tr>
        </thead>
        <tbody>
          {computation.days.flatMap((day) =>
            day.entries.map((entry, index) => (
              <tr key={entry.id} data-testid={`timesheet-entry-${entry.id}`}>
                <td>{index === 0 ? dayjs(day.date).format('DD/MM') : ''}</td>
                <td>{index === 0 ? dayjs(day.date).format('ddd') : ''}</td>
                <td>
                  {[entry.shiftName, entry.note].filter(Boolean).join(' — ')}
                  {entry.employeeNote && <div className="timesheet-detail__dispute">“{entry.employeeNote}”</div>}
                  <EntryScheduleLabel info={view.schedule?.[entry.id]} timezone={tz} />
                  <Button
                    size="sm"
                    kind="ghost"
                    data-testid={`timesheet-link-shift-${entry.id}`}
                    onClick={() => setModal({ kind: 'link', entry, date: day.date })}
                  >
                    {t('schedule.linkShift')}
                  </Button>
                </td>
                <td title={entry.edited ? t('timesheet.actions.edit') : undefined}>
                  {localTime(entry.clockInAt, tz)}
                  {entry.edited && ' ✎'}
                  {entry.geoFlagged && (
                    <Tag size="sm" type="red">
                      {/* Distance is appended as its own text node rather than an i18next
                          interpolation value — same reasoning as timesheet-since in
                          TimesheetCard: a number is not translatable content, and keeping
                          it out of the {{}} placeholder means a mocked t() still renders
                          the two together intelligibly. */}
                      {entry.clockInDistanceMeters === null
                        ? t('timesheet.geoNoPosition')
                        : `${entry.clockInDistanceMeters} m ${t('timesheet.geoFlag')}`}
                    </Tag>
                  )}
                </td>
                <td>{entry.clockOutAt ? localTime(entry.clockOutAt, tz) : '…'}</td>
                <td>
                  {index === 0 ? `${formatMinutes(day.paidBreakMin)} / ${formatMinutes(day.unpaidBreakMin)}` : ''}
                </td>
                <td>{formatMinutes(entry.payableMin)}</td>
                <td>{index === 0 ? money.format(day.pay) : ''}</td>
                {open && (
                  <td className="timesheet-detail__row-actions">
                    <Button size="sm" kind="ghost" onClick={() => setModal({ kind: 'entry', entry, date: day.date })}>
                      {t('timesheet.actions.edit')}
                    </Button>
                    <Button
                      size="sm"
                      kind="ghost"
                      disabled={entry.open}
                      onClick={() => setModal({ kind: 'break', entry })}
                    >
                      {t('timesheet.actions.addBreak')}
                    </Button>
                    <Button size="sm" kind="danger--ghost" onClick={() => setModal({ kind: 'delete', entry })}>
                      {t('timesheet.actions.delete')}
                    </Button>
                    {entry.breaks.map((b) => (
                      <Button
                        key={b.id}
                        size="sm"
                        kind="danger--ghost"
                        onClick={() => actions.deleteBreak.mutate(b.id)}
                      >
                        ✕ {localTime(b.startAt, tz)}–{b.endAt ? localTime(b.endAt, tz) : '…'}
                      </Button>
                    ))}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      <section className="timesheet-detail__totals">
        <p>
          {t('timesheet.totalHours')}: <strong>{formatMinutes(computation.totals.payableMin)}</strong>
        </p>
        <p>
          {t('timesheet.columns.rate')}: <strong>{money.format(view.hourlyRate)}</strong>
        </p>
        <p>
          {t('timesheet.columns.multipliers')}:{' '}
          {Object.entries(computation.totals.byMultiplier)
            .map(([m, min]) => `×${m} ${formatMinutes(min)}`)
            .join(' · ')}
        </p>
        <p data-testid="timesheet-gross">
          {t('timesheet.grossPay')}: <strong>{money.format(computation.totals.pay)}</strong>
        </p>
      </section>

      <section className="timesheet-detail__signoff">
        <h3>{t('timesheet.signOff.title')}</h3>
        <div data-testid="timesheet-signoff-employee">
          <strong>{t('timesheet.signOff.employee')}: </strong>
          {timesheet.employeeConfirmedAt ? (
            <>
              {t('timesheet.signOff.confirmedAt', { date: stamp(timesheet.employeeConfirmedAt) })}{' '}
              <span>({t(`timesheet.signOff.sources.${timesheet.employeeConfirmedSource}`)})</span>
            </>
          ) : (
            t('timesheet.notConfirmed')
          )}
          <div className="timesheet-detail__signature">
            {t('timesheet.signOff.signature')}: ____________ {t('timesheet.signOff.date')}: ________
          </div>
        </div>
        <div>
          <strong>{t('timesheet.signOff.manager')}: </strong>
          {timesheet.approvedAt
            ? t('timesheet.signOff.approvedAt', { name: view.approvedByName, date: stamp(timesheet.approvedAt) })
            : '—'}
          <div className="timesheet-detail__signature">
            {t('timesheet.signOff.signature')}: ____________ {t('timesheet.signOff.date')}: ________
          </div>
        </div>
        {timesheet.paidAt && (
          <div>
            <strong>{t('timesheet.signOff.payment')}: </strong>
            {t('timesheet.signOff.paidAt', {
              date: stamp(timesheet.paidAt),
              method: t(`timesheet.methods.${timesheet.paymentMethod}`),
            })}
          </div>
        )}
      </section>

      {modal?.kind === 'entry' && <EntryModal view={view} entry={modal.entry} date={modal.date} onClose={close} />}
      {modal?.kind === 'break' && <BreakModal view={view} entry={modal.entry} onClose={close} />}
      {modal?.kind === 'pay' && <PayModal view={view} onClose={close} />}
      {modal?.kind === 'reopen' && <ReopenModal view={view} onClose={close} />}
      {modal?.kind === 'link' && (
        <LinkShiftModal
          timesheetId={timesheet.id}
          employeeId={header.employeeId}
          entryId={modal.entry.id}
          date={modal.date}
          timezone={tz}
          current={view.schedule?.[modal.entry.id]?.link?.assignmentId ?? null}
          onClose={close}
        />
      )}
      {modal?.kind === 'delete' && (
        <Modal open passiveModal modalHeading={t('timesheet.actions.delete')} onRequestClose={close}>
          <Button kind="danger" onClick={() => actions.deleteEntry.mutate(modal.entry.id, { onSuccess: close })}>
            {t('timesheet.actions.delete')}
          </Button>
        </Modal>
      )}
    </div>
  );
};

export default EmployeeTimesheetDetail;
